// The space theater's unrolled-Kepler kernel — exact JS mirror of
// internal/orbit/orbit.go. True 2D Keplerian motion around the Brick Moon,
// integrated in unrolled cylindrical coordinates: x is the wrapped map
// coordinate (the orbit direction), h altitude above the datum, z the
// cross-track axis.
//
// Parity is the TERRAIN mirror's standard, not the flight model's: only
// IEEE-754 float64 add/sub/mul/div/sqrt and comparisons — no trig, no exp,
// no abs — so the two sides are BIT-IDENTICAL. The client leans on that
// harder than the plane's prediction model ever did: the orbital chart's
// plotted path and the fire-control mark are this kernel propagated ahead,
// and they must land where the server says the shell lands. Verified by
// tools/check_orbit_parity.mjs; keep the two files in lockstep.

/** One hull's (or shell's) translational state in unrolled map coordinates. */
export function newOrbitState(x = 0, h = 0, z = 0, vx = 0, vh = 0, vz = 0) {
  return { x, h, z, vx, vh, vz };
}

/**
 * Kernel accelerations at a state. mu = g0·R² (the moon's gravitational
 * parameter), R the datum radius (wrapM / 2π).
 *
 *   ax = −2·vh·vx / r        angular momentum: climbing coasts you backward
 *   ah = r·vx²/R² − mu/r²    centrifugal lift minus gravity
 *   az = −(mu/r³)·z          out-of-plane libration, self-centring
 */
export function orbitAccel(s, mu, R) {
  const r = R + s.h;
  return {
    ax: (-2 * s.vh * s.vx) / r,
    ah: (r * s.vx * s.vx) / (R * R) - mu / (r * r),
    az: -(mu / (r * r * r)) * s.z,
  };
}

/**
 * One fixed tick of semi-implicit Euler — velocity first, then position with
 * the NEW velocity, the engine's integrator. Symplectic for this force: a
 * coasting orbit holds its energy for a whole round. tx/th/tz are thrust
 * accelerations (m/s²); pass zeros to coast.
 */
export function stepOrbit(s, mu, R, wrapM, dt, tx = 0, th = 0, tz = 0) {
  const a = orbitAccel(s, mu, R);
  s.vx += (a.ax + tx) * dt;
  s.vh += (a.ah + th) * dt;
  s.vz += (a.az + tz) * dt;
  s.x += s.vx * dt;
  s.h += s.vh * dt;
  s.z += s.vz * dt;
  // One conditional wrap, not a modulo — bit-identical with the Go side.
  if (s.x >= wrapM) s.x -= wrapM;
  else if (s.x < 0) s.x += wrapM;
}

/**
 * Shortest signed x distance from b to a on the wrapped map, in
 * (−wrapM/2, +wrapM/2]. Every space bearing/range goes through this — the
 * enemy just across the seam is close, not a map away.
 */
export function wrapDeltaX(a, b, wrapM) {
  let d = a - b;
  if (d > wrapM * 0.5) d -= wrapM;
  else if (d <= -wrapM * 0.5) d += wrapM;
  return d;
}

/** Specific orbital energy (v²/2 − mu/r) — the conserved coasting quantity. */
export function orbitEnergy(s, mu, R) {
  const r = R + s.h;
  const vt = s.vx * (r / R);
  return 0.5 * (vt * vt + s.vh * s.vh + s.vz * s.vz) - mu / r;
}

/** Unrolled-x rate of a circular orbit at altitude h: √(mu/r) · R/r. */
export function circularVX(mu, R, h) {
  const r = R + h;
  return Math.sqrt(mu / r) * (R / r);
}
